import React, { useState } from "react";
import { Select, Table, Modal, Button } from "antd";
import { ExportToExcel } from "../../../../utils";

export const ChannelCategoryFilter = ({ data }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState([]);

  const uniqueChannels = Array.from(new Set(data?.map((item) => item.channel)));
  const uniquePaymenttype = Array.from(
    new Set(data?.map((item) => item.paymenttype))
  );
  const uniqueCategories = Array.from(
    new Set(data?.flatMap((item) => item.products.map((i) => i.category)))
  );
  const [filterType, setFilterType] = useState("channel");
  const [selectedFilter, setSelectedFilter] = useState(null);

  const filteredData = data?.filter((item) => {
    if (filterType === "channel") {
      if (selectedFilter && selectedFilter !== item.channel) {
        return false;
      }
    }
    if (filterType === "category") {
      if (selectedFilter && selectedFilter !== item.category) {
        return false;
      }
    }
    if (filterType === "paymenttype") {
      if (selectedFilter && selectedFilter !== item.paymenttype) {
        return false;
      }
    }
    return true;
  });

  const getTotalByChannel = (channel) => {
    return filteredData
      .filter((item) => item.channel === channel)
      .reduce((total, item) => total + item.saleAmount, 0);
  };

  const getTotalByCategory = (category) => {
    return data
      .filter((item) => item.items?.some((i) => i.category === category))
      .flatMap((item) => item.items?.filter((i) => i.category === category))
      .reduce((total, item) => total + item.total, 0);
  };

  const getTotalByPaymenttype = (paymenttype) => {
    return filteredData
      .filter((item) => item.paymenttype === paymenttype)
      .reduce((total, item) => total + item.saleAmount, 0);
  };

  const handleFilterTypeChange = (value) => {
    setFilterType(value);
    setSelectedFilter(null);
  };

  const getDetailsByChannel = (channel) => {
    const items = filteredData
      .filter((item) => item.channel === channel)
      .flatMap((item) => item.products);

    const details = items.reduce((acc, item) => {
      const key = `${item.category}-${item.brand}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(details)?.map(([key, count]) => {
      const [category, brand] = key.split("-");
      return { category, brand, count };
    });
  };

  const getDetailsByCategory = (category) => {
    const items = data
      .filter((item) => item.items?.some((i) => i.category === category))
      .flatMap((item) => item.items?.filter((i) => i.category === category));

    const details = items?.reduce((acc, item) => {
      const key = `${item.category}-${item.brand}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(details)?.map(([key, count]) => {
      const [category, brand] = key.split("-");
      return { category, brand, count };
    });
  };

  const getDetailsByPaymenttype = (paymenttype) => {
    const items = filteredData
      .filter((item) => item.paymenttype === paymenttype)
      .flatMap((item) => item.items);

    const details = items?.reduce((acc, item) => {
      const key = `${item.category}-${item.brand}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(details)?.map(([key, count]) => {
      const [category, brand] = key.split("-");
      return { category, brand, count };
    });
  };

  const showModal = (details) => {
    setModalData(details);
    setIsModalVisible(true);
  };

  const totalSales = filteredData?.reduce(
    (total, item) => total + item.saleAmount,
    0
  );

  const columns = [
    {
      title: (
        <div>
          <Select
            defaultValue={filterType}
            onChange={handleFilterTypeChange}
            className="ml-4 w-20 lg:w-32"
          >
            <Select.Option value="channel">Canal</Select.Option>
            <Select.Option value="category">Categoría</Select.Option>
            <Select.Option value="paymenttype">Tipo de pago</Select.Option>
          </Select>
        </div>
      ),
      dataIndex:
        filterType === "channel"
          ? "channel"
          : filterType === "category"
            ? "category"
            : "paymenttype",
      key:
        filterType === "channel"
          ? "channel"
          : filterType === "category"
            ? "category"
            : "paymenttype",
    },
    {
      title: "Total Ventas",
      dataIndex: "total",
      key: "total",
      render: (text) => `S/ ${text.toFixed(2)}`,
      sorter: (a, b) => a.total - b.total,
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "descend",
    },
    {
      title: "% Total Ventas",
      dataIndex: "total",
      key: "total",
      render: (_, record) => {
        const total = ((record.total / totalSales) * 100).toFixed(2);
        return `${total}%`;
      },
    },
    {
      title: "Detalles",
      key: "details",
      render: (text, record) => (
        <Button onClick={() => showModal(record.details)} className="btnCancel">
          Detalles
        </Button>
      ),
      width: 120,
    },
  ];

  const columnsDetails =
    filterType === "category"
      ? [
        {
          title: "Marca",
          dataIndex: "brand",
          key: "brand",
        },
        {
          title: "Cantidad",
          dataIndex: "count",
          key: "count",
        },
      ]
      : [
        {
          title: "Categoría",
          dataIndex: "category",
          key: "category",
        },
        {
          title: "Marca",
          dataIndex: "brand",
          key: "brand",
        },
        {
          title: "Cantidad",
          dataIndex: "count",
          key: "count",
        },
      ];

  const dataSource =
    filterType === "channel"
      ? uniqueChannels?.map((channel) => ({
        channel,
        total: getTotalByChannel(channel),
        details: getDetailsByChannel(channel),
        key: channel,
      }))
      : filterType === "category"
        ? uniqueCategories?.map((category) => ({
          category,
          total: getTotalByCategory(category),
          details: getDetailsByCategory(category),
          key: category,
        }))
        : uniquePaymenttype?.map((paymenttype) => ({
          paymenttype,
          total: getTotalByPaymenttype(paymenttype),
          details: getDetailsByPaymenttype(paymenttype),
          key: paymenttype,
        }));

  const exportData = dataSource?.map((item) => {
    const type = item[filterType];
    return {
      type,
      total: `S/ ${item.total.toFixed(2)}`,
      percentage: `${((item.total / totalSales) * 100).toFixed(2)}% `,
    };
  });

  const typeTranslation = {
    channel: "Canal",
    category: "Categoria",
    paymenttype: "Typo de pago",
  };

  const COLUMNS = [
    {
      title: typeTranslation[filterType],
      dataIndex: "type",
      key: "type",
    },
    { title: "Total", dataIndex: "total", key: "total" },
    { title: "Porcentaje", dataIndex: "percentage", key: "percentage" },
  ];

  return (
    <div className="w-full pt-4 pr-4 pl-4">
      <div className="overflow-x-auto h-96">
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
        />
      </div>
      <Modal
        title="Detalles"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <div className="overflow-x-auto h-96">
          <Table
            dataSource={modalData}
            columns={columnsDetails}
            pagination={false}
          />
        </div>
      </Modal>
      <div
        className="flex justify-end my-2"
      >
        <ExportToExcel
          data={exportData}
          columns={COLUMNS}
          fileName={`resumen${typeTranslation[filterType]}`}
        />
      </div>
    </div>
  );
};
